import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from "@cloudscape-design/components";
import { API_BASE, AUTH_MODE } from "../config";
import { LOGIN_REDIRECT_KEY, redirectToLogin } from "../api/auth";

const isInternalAuth = AUTH_MODE === "internal";
const DEFAULT_REDIRECT_PATH = "/";

const getRedirectTarget = () => {
  const params = new URLSearchParams(window.location.search);
  const redirectParam = params.get("redirect");
  const storedRedirect = window.localStorage.getItem(LOGIN_REDIRECT_KEY);
  const candidate = redirectParam || storedRedirect;

  if (storedRedirect) {
    window.localStorage.removeItem(LOGIN_REDIRECT_KEY);
  }

  if (candidate && candidate.startsWith("/")) {
    return candidate;
  }

  return DEFAULT_REDIRECT_PATH;
};

function Login({ onAuthChanged }) {
  const history = useHistory();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isInternalAuth) {
      redirectToLogin("External auth configured; redirecting.");
      return;
    }

    const token = window.localStorage.getItem("access_token");
    if (token) {
      history.replace(getRedirectTarget());
    }
  }, [history]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError("");

    const trimmedUsername = username.trim();
    if (!trimmedUsername || !password) {
      setError("Username and password are required.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: trimmedUsername,
          password: password,
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (err) {
        data = null;
      }

      if (!response.ok || !data?.ok) {
        setError(data?.error || "Login failed.");
        return;
      }

      if (data.id_token) {
        window.localStorage.setItem("id_token", data.id_token);
      }
      if (data.access_token) {
        window.localStorage.setItem("access_token", data.access_token);
      }
      if (data.refresh_token) {
        window.localStorage.setItem("refresh_token", data.refresh_token);
      }

      if (typeof onAuthChanged === "function") {
        onAuthChanged();
      }

      history.replace(getRedirectTarget());
    } catch (err) {
      console.error(err);
      setError("Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box padding={{ vertical: "xxl", horizontal: "l" }}>
      <Container header={<Header variant="h1">Sign in</Header>}>
        <form onSubmit={handleSubmit}>
          <SpaceBetween size="l">
            {error ? (
              <Alert type="error" dismissible onDismiss={() => setError("")}>
                {error}
              </Alert>
            ) : null}
            <FormField label="Username">
              <Input
                value={username}
                onChange={(event) => setUsername(event.detail.value)}
                autoFocus
                placeholder="Enter your username"
              />
            </FormField>
            <FormField label="Password">
              <Input
                value={password}
                type="password"
                onChange={(event) => setPassword(event.detail.value)}
                placeholder="Enter your password"
              />
            </FormField>
            <FormField>
              <Button
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Sign in
              </Button>
            </FormField>
          </SpaceBetween>
        </form>
      </Container>
    </Box>
  );
}

export default Login;
